// Panel de la página Configuración. Extraído de pages/Configuracion.tsx
// (modularización 2026-06-11) sin cambios de comportamiento.
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api, type Marca, type Proveedor } from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

// ============================================================================
// MARCAS
// ============================================================================

export default function MarcasPanel() {
  const toast = useToast();
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [nombreNueva, setNombreNueva] = useState("");
  const [proveedorNueva, setProveedorNueva] = useState<number | "">("");
  const [creando, setCreando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [confirmDel, setConfirmDel] = useState<Marca | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const [ms, ps] = await Promise.all([
        api.get<Marca[]>("/api/catalogos/marcas?incluir_inactivas=true"),
        api.get<Proveedor[]>("/api/proveedores"),
      ]);
      setMarcas(ms);
      setProveedores(ps);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function crear(e: FormEvent) {
    e.preventDefault();
    if (!nombreNueva.trim()) return;
    setCreando(true);
    try {
      const m = await api.post<Marca>("/api/catalogos/marcas", {
        nombre: nombreNueva.trim(),
        proveedor_id: proveedorNueva === "" ? null : proveedorNueva,
      });
      toast.success("Marca creada", m.nombre);
      setNombreNueva("");
      setProveedorNueva("");
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  async function setProveedor(m: Marca, proveedorId: number | null) {
    try {
      await api.patch<Marca>(`/api/catalogos/marcas/${m.id}`, {
        proveedor_id: proveedorId,
      });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo asociar", msg);
    }
  }

  async function toggleActivo(m: Marca) {
    try {
      await api.patch<Marca>(`/api/catalogos/marcas/${m.id}`, { activo: !m.activo });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  function startEdit(m: Marca) {
    setEditandoId(m.id);
    setEditNombre(m.nombre);
  }

  async function saveEdit(m: Marca) {
    if (!editNombre.trim() || editNombre === m.nombre) {
      setEditandoId(null);
      return;
    }
    try {
      await api.patch<Marca>(`/api/catalogos/marcas/${m.id}`, { nombre: editNombre.trim() });
      toast.success("Marca renombrada");
      setEditandoId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo renombrar", msg);
    }
  }

  async function eliminar(m: Marca) {
    try {
      await api.del(`/api/catalogos/marcas/${m.id}`);
      toast.success("Marca eliminada", m.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  return (
    <>
      <form onSubmit={crear} className="card flex flex-wrap items-end gap-3">
        <div className="w-full sm:flex-1 sm:min-w-[200px]">
          <label className="text-xs uppercase tracking-wide text-zinc-400">Nueva marca</label>
          <input
            type="text"
            className="input mt-1"
            value={nombreNueva}
            onChange={(e) => setNombreNueva(e.target.value)}
            placeholder="ej. Ruptela, Queclink, JIMI, Concox"
          />
        </div>
        <div className="w-full sm:min-w-[180px]">
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Proveedor (opcional)
          </label>
          <select
            className="input mt-1"
            value={proveedorNueva}
            onChange={(e) => setProveedorNueva(e.target.value ? parseInt(e.target.value) : "")}
          >
            <option value="">— sin proveedor —</option>
            {proveedores
              .filter((p) => p.activo)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
          </select>
        </div>
        <button type="submit" disabled={!nombreNueva.trim() || creando} className="btn-primary disabled:opacity-50 w-full sm:w-auto">
          {creando ? "Creando..." : "+ Agregar"}
        </button>
      </form>

      <div className="card">
        {loading ? (
          <SkeletonRows rows={4} cols={3} />
        ) : marcas.length === 0 ? (
          <p className="text-sm text-zinc-500">No hay marcas todavía. Agrega la primera arriba.</p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {marcas.map((m) => (
              <li key={m.id} className={`py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${m.activo ? "" : "opacity-50"}`}>
                {editandoId === m.id ? (
                  <input
                    type="text"
                    autoFocus
                    className="input flex-1"
                    value={editNombre}
                    onChange={(e) => setEditNombre(e.target.value)}
                    onBlur={() => saveEdit(m)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(m);
                      if (e.key === "Escape") setEditandoId(null);
                    }}
                  />
                ) : (
                  <span className="text-zinc-200 flex-1 min-w-0 truncate">{m.nombre}</span>
                )}
                <select
                  value={m.proveedor_id ?? ""}
                  aria-label={`Proveedor de ${m.nombre}`}
                  onChange={(e) =>
                    setProveedor(m, e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="input text-xs py-1 w-full sm:w-auto sm:min-w-[140px]"
                >
                  <option value="">— sin proveedor —</option>
                  {proveedores
                    .filter((p) => p.activo || p.id === m.proveedor_id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre}{!p.activo ? " (inactivo)" : ""}
                      </option>
                    ))}
                </select>
                <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
                  <button onClick={() => toggleActivo(m)} className="btn-ghost text-xs">
                    {m.activo ? "Desactivar" : "Activar"}
                  </button>
                  <button onClick={() => startEdit(m)} className="btn-ghost text-xs">Renombrar</button>
                  <button
                    onClick={() => setConfirmDel(m)}
                    className="btn-ghost text-xs text-red-400 hover:text-red-300"
                  >
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar "${confirmDel.nombre}"`}
          description="También se borrarán todos sus modelos. Los casos que ya tienen este texto guardado no se modifican."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </>
  );
}

